<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ArabicText from '@/components/quran/ArabicText.vue';
import { useQuranPlayer } from '@/composables/quran/useQuranPlayer';
import { useQuranSettings, visibleTranslations } from '@/composables/quran/useQuranSettings';
import { toArabicDigits } from '@/utils/quran/marks';

/**
 * The queue as the text being recited, like a lyrics view: the current ayah
 * lit and centred, the rest dimmed around it; tap any ayah to recite from it.
 * If the reader scrolls away, following pauses until they come back.
 */
const { t, locale } = useI18n();
const player = useQuranPlayer();
const settings = useQuranSettings();
const state = player.state;

const scroller = ref(null);
const following = ref(true);
let programmatic = false;

const translations = computed(() => {
  const chosen = visibleTranslations(settings.translation);
  return chosen.length ? chosen : [locale.value];
});

function centre(behavior = 'smooth') {
  const el = scroller.value?.querySelector(`[data-index="${state.index}"]`);
  if (!el) return;
  programmatic = true;
  scroller.value.scrollTo({ top: el.offsetTop - scroller.value.clientHeight * 0.3, behavior });
  setTimeout(() => (programmatic = false), 700);
}

function onScroll() {
  if (!programmatic) following.value = false;
}

function backToCurrent() {
  following.value = true;
  centre();
}

watch(() => state.index, async () => {
  if (!following.value) return;
  await nextTick();
  centre();
});

onMounted(() => nextTick(() => centre('auto')));
</script>

<template>
  <div class="relative h-full min-h-0">
    <div ref="scroller" class="h-full overflow-y-auto overscroll-contain px-1 pb-[40vh] [mask-image:linear-gradient(to_bottom,transparent,black_6%,black_88%,transparent)]" @scroll.passive="onScroll">
      <div class="h-[8vh]" aria-hidden="true" />
      <button
        v-for="(item, index) in state.queue"
        :key="item.key"
        type="button"
        :data-index="index"
        :aria-current="index === state.index ? 'true' : undefined"
        :class="[
          'block w-full text-left rounded-3xl px-4 sm:px-6 py-4 mb-2 transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deen-gold',
          index === state.index ? 'bg-white/[0.06] opacity-100' : 'opacity-35 hover:opacity-70',
        ]"
        @click="player.jumpTo(index)"
      >
        <span class="flex items-center gap-2 mb-1 text-xs font-semibold" :class="index === state.index ? 'text-deen-gold' : 'text-white/60'">
          <span class="tabular-nums">{{ item.key }}</span>
          <span v-if="index === state.index && state.repeatCount > 0" class="px-1.5 rounded-md bg-deen-gold/20">
            {{ t('quran.player.repeatRound', { n: state.repeatCount + 1 }) }}
          </span>
        </span>
        <ArabicText
          :text="item.arabic"
          :size="index === state.index ? 4 : 2"
          align="right"
          class="!text-white transition-all"
        >
          <span class="font-quran text-deen-gold/80 select-none whitespace-nowrap" aria-hidden="true">﴿{{ toArabicDigits(item.number) }}﴾</span>
        </ArabicText>
        <div v-if="index === state.index" class="mt-3 space-y-3">
          <p
            v-for="lang in translations"
            :key="lang"
            :lang="lang"
            class="border-l-2 border-deen-gold/60 pl-4 font-reading text-[17px] sm:text-lg text-white/85 leading-[1.75] max-w-[62ch]"
          >{{ item.translation[lang] }}</p>
        </div>
      </button>
    </div>

    <Transition enter-active-class="transition duration-200" enter-from-class="opacity-0 translate-y-2" leave-active-class="transition duration-150" leave-to-class="opacity-0">
      <button
        v-if="!following"
        type="button"
        class="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-deen-gold text-deen-ink text-sm font-semibold shadow-lg"
        @click="backToCurrent"
      >
        <iconify-icon icon="solar:target-linear" aria-hidden="true" />{{ t('quran.player.backToCurrent') }}
      </button>
    </Transition>
  </div>
</template>
